// src/pages/Counselling/Cases/[CaseDetails]/Tabs/AnswersTab.tsx
/**
 * The need's custom-field answers carried onto the case at approval.
 *
 * The counselling twin of `Grow/Registration/[RegistrationDetails]/Tabs/CourseAnswersTab.tsx`. These are
 * the answers the requester gave to the questions the need itself asks (defined per need under
 * `/LookUp/ActsCareCounseling/Needs` → Manage custom form), copied across from the request when it was
 * approved.
 *
 * ⚠️ **Read-only, deliberately.** `CareCaseService` gained `addCustomData` / `updateCustomData`
 * alongside `customData` (SDK 0.0.52), but editing an answer means editing what someone *said* on a
 * public form, which is evidence rather than a working field. Displaying it was the point; add editing
 * only if the care office actually asks.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, Center, Group, Loader, Stack, Table, Text, Badge } from '@mantine/core';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useCareCaseService, type CareCaseCustomDataModelView,
} from '@actschurch/ccms-sdk';
import { formatSDKDate } from '@/utils/dateTime';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';

interface AnswersTabProps {
  caseID: number;
  active: boolean;
}

/**
 * The stored value, whichever typed column it landed in.
 *
 * ⚠️ Ordered so the typed columns win over the raw one: `value` is the unparsed original, and for a
 * boolean it reads "true"/"false" rather than Yes/No.
 */
function formatValue(row: CareCaseCustomDataModelView): string {
  if (row.valueBoolean !== null && row.valueBoolean !== undefined) {
    return row.valueBoolean ? 'Yes' : 'No';
  }
  if (row.valueDecimal !== null && row.valueDecimal !== undefined) return String(row.valueDecimal);
  if (row.valueNumber !== null && row.valueNumber !== undefined) return String(row.valueNumber);
  if (row.valueDateTime) return formatSDKDate(row.valueDateTime);
  return (row.valueText ?? row.value ?? '').trim() || '—';
}

export default function AnswersTab({ caseID, active }: AnswersTabProps) {
  const careCaseService = useCareCaseService();
  const [rows, setRows] = useState<CareCaseCustomDataModelView[] | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchAnswers = useCallback(async () => {
    if (!caseID) return;
    setLoading(true);
    try {
      const res = await careCaseService.customData(caseID);
      // ⚠️ Read through the envelope — this API wraps some payloads and not others.
      const list = ((res as unknown as { data?: CareCaseCustomDataModelView[] })?.data ?? res);
      setRows(Array.isArray(list) ? list.filter((r) => r.isActive) : []);
    } catch (e) {
      console.error('[CaseAnswers] failed to load:', e);
      toast.error('Error', 'Could not load the answers for this case.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [caseID, careCaseService]);

  useEffect(() => { if (active && rows === null) fetchAnswers(); }, [active, rows, fetchAnswers]);

  if (loading && rows === null) {
    return <Center py="xl"><Loader /></Center>;
  }

  if (rows !== null && rows.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        entityLabel="answers"
        /* Not an error: a need with no custom fields asks nothing, so there is nothing to carry. */
        description="This need asks no extra questions, or the request was approved before they were added."
      />
    );
  }

  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" mb="sm">
        <div>
          <Text fw={700}>Additional answers</Text>
          <Text size="sm" c="dimmed">
            What the requester answered to the need&apos;s own questions, carried over on approval.
          </Text>
        </div>
      </Group>

      <Stack gap="xs">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Question</Table.Th>
              <Table.Th>Answer</Table.Th>
              <Table.Th>Counselee</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(rows ?? []).map((row) => (
              <Table.Tr key={row.careCaseCustomDataID}>
                <Table.Td>
                  {/* The definition is embedded, so no second lookup is needed for the label. */}
                  <Text size="sm" fw={600}>{row.field?.label ?? row.field?.fieldKey ?? '—'}</Text>
                  {row.field?.category && (
                    <Text size="xs" c="dimmed">{row.field.category}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{formatValue(row)}</Text>
                </Table.Td>
                <Table.Td>
                  {/* null memberID means the answer is about the case as a whole, not one person. */}
                  {row.memberID
                    ? <Badge size="sm" variant="light">{row.memberName ?? `Member #${row.memberID}`}</Badge>
                    : <Text size="xs" c="dimmed">Whole case</Text>}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
